import { useCallback, useEffect, useRef, useState } from 'react';

import {
  listQuickInferenceSessions,
  type QuickInferenceSessionListItem,
  type QuickInferenceSessionStatusFilter,
} from '../../../helpers/api/quick-inference/quick-inference';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export function useQuickInferenceSessions() {
  const [items, setItems] = useState<QuickInferenceSessionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    active: 0,
    success: 0,
    failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<QuickInferenceSessionStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const data = await listQuickInferenceSessions({
          limit: PAGE_SIZE,
          offset,
          status: filter,
          search: debouncedSearch,
        });

        if (requestId !== requestIdRef.current) return;

        setTotal(data.total);
        setStatusCounts(data.status_counts);
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      } catch (err: any) {
        if (requestId !== requestIdRef.current) return;
        setError(err?.message || 'Failed to load sessions');
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [debouncedSearch, filter]
  );

  const refresh = useCallback(async () => {
    await fetchPage(0, false);
  }, [fetchPage]);

  const refreshLoaded = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setError(null);

    try {
      const data = await listQuickInferenceSessions({
        limit: Math.max(items.length, PAGE_SIZE),
        offset: 0,
        status: filter,
        search: debouncedSearch,
      });

      if (requestId !== requestIdRef.current) return;

      setTotal(data.total);
      setStatusCounts(data.status_counts);
      setItems(data.items);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.message || 'Failed to load sessions');
    }
  }, [debouncedSearch, filter, items.length]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || items.length >= total) return;
    await fetchPage(items.length, true);
  }, [fetchPage, items.length, loading, loadingMore, total]);

  useEffect(() => {
    fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    const hasActive = items.some(
      (item) => item.status === 'pending' || item.status === 'processing'
    );
    if (!hasActive) return;
    const timer = window.setInterval(() => {
      refreshLoaded();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items, refreshLoaded]);

  const hasMore = items.length < total;

  return {
    items,
    total,
    statusCounts,
    loading,
    loadingMore,
    error,
    filter,
    setFilter,
    search,
    setSearch,
    hasMore,
    refresh,
    loadMore,
    removeSession: (sessionId: string) => {
      setItems((prev) => prev.filter((item) => item.id !== sessionId));
      setTotal((prev) => Math.max(0, prev - 1));
      setStatusCounts((prev) => ({
        ...prev,
        all: Math.max(0, prev.all - 1),
      }));
    },
  };
}
